/**
 * Follower Tracking (`/follower-tracking`) — vijf KPI's, vier tabellen, de
 * dag×uur-heatmap en "Best times to post", zoals de huidige pagina.
 *
 * Endpoints exact zoals de bundle:
 *   dashboard/follower-growth?days=<venster>     (default 1 = Today)
 *   dashboard/follower-heatmap?days=<30|7|90|365>  (eigen keuzeknoppen)
 *   dashboard/top-spenders?limit=1000
 *   models/platform-stats
 */
import { useEffect, useMemo, useState } from 'react'
import { ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { isBeheer, useMij } from '../lib/rechten'
import { eur, num } from '../lib/format'

interface Growth {
  per_model: {
    model_slug: string; avatar_url: string | null
  stage_name: string | null; platform: string
    current_count: number; starting_count: number; new_followers: number
  }[]
  totals: { new_followers: number; current: number }
  period_days: number
}
interface Heatmap {
  matrix: number[][]
  totals_by_hour: number[]
  totals_by_weekday: number[]
  total: number
  window_days: number
}
interface Stats {
  totals: { followers: number; paid_fans: number; paid_revenue_eur: number }
  totals_by_platform: Record<string, { followers: number; paid_fans: number }>
  per_model: { model_slug: string; platform: string; follower_count: number; paid_fan_count: number }[]
}

/**
 * De heatmap, eventueel voor één model (04-10-2026). De server neemt één
 * `model_slug`; een model met FC én F2F heeft er twee, dus dan vragen we beide
 * en tellen de matrices op.
 */
function useHeatmap(basis: string | null, dagen: string, slugs: string[] | null, platform: string) {
  const [data, setData] = useState<Heatmap | null>(null)
  const [error, setError] = useState<unknown>(null)
  const sleutel = `${basis}|${dagen}|${(slugs ?? []).join(',')}|${platform}`
  useEffect(() => {
    if (!basis) return
    let levend = true
    const plat = platform === 'all' ? '' : `&platform=${platform}`
    const paden = slugs && slugs.length
      ? slugs.map((s) => `${basis}/follower-heatmap?days=${dagen}${plat}&model_slug=${encodeURIComponent(s)}`)
      : [`${basis}/follower-heatmap?days=${dagen}${plat}`]
    Promise.all(paden.map((pad) => api.get<Heatmap>(pad))).then((delen) => {
      if (!levend) return
      const som = (a: number[], b: number[]) => a.map((v, i) => v + (b[i] ?? 0))
      setData(delen.reduce((acc, d) => ({
        matrix: acc.matrix.map((rij, i) => som(rij, d.matrix[i] ?? [])),
        totals_by_hour: som(acc.totals_by_hour, d.totals_by_hour),
        totals_by_weekday: som(acc.totals_by_weekday, d.totals_by_weekday),
        total: acc.total + d.total,
        window_days: d.window_days,
      })))
      setError(null)
    }).catch((e) => { if (levend) setError(e) })
    return () => { levend = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel])
  return { data, error, loading: data === null && !error }
}
interface Spender {
  fan_id: string; username: string; model: string; gross_spend: number
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]
const HEAT_WINDOWS = [
  { value: '7', label: '7d' }, { value: '30', label: '30d' },
  { value: '90', label: '90d' }, { value: '365', label: '1y' },
]
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default function FollowerTracking() {
  const [platform, setPlatform] = useState('all')
  const [days, setDays] = useState('1')
  const [heatDays, setHeatDays] = useState('30')
  const [showAll, setShowAll] = useState(false)

  /* 30-09-2026 (Jordy): een chatter met "Allow Follower Tracking page" ziet
     deze pagina via de chatterroutes — dezelfde berekening, alleen zijn eigen
     modellen, zonder omzet. De /dashboard-routes zijn voor beheer (403). */
  const mij = useMij()
  const basis = mij.data ? (isBeheer(mij.data) ? '/api/admin/dashboard' : '/api/admin/chatter') : null
  const growth = useApi<Growth>(
    basis ? `${basis}/follower-growth?days=${days}` : null, [days, basis])
  // 04-10-2026 (Jordy): filter op één model voor de hele pagina, ook de
  // kaarten en de heatmap; de keuze staat in het adres en geldt alleen hier.
  const filter = useModelFilter()
  const heat = useHeatmap(basis, heatDays, filter.slugs, platform)
  const stats = useApi<Stats>(basis ? '/api/admin/models/platform-stats' : null, [basis])
  const spenders = useApi<{ spenders: Spender[]; total: number }>(
    basis ? `${basis}/top-spenders?limit=1000` : null, [basis])

  const rows = useMemo(() => {
    const all = growth.data?.per_model ?? []
    // De bron kan een slug twee keer leveren (fc_aliceimonta); de oude pagina
    // ontdubbelt daarop en houdt de rij met de meeste volgers.
    const perSlug = new Map<string, Growth['per_model'][number]>()
    for (const m of all) {
      const prev = perSlug.get(m.model_slug)
      if (!prev || m.current_count > prev.current_count) perSlug.set(m.model_slug, m)
    }
    return [...perSlug.values()]
      .filter((m) => (platform === 'all' ? true : m.platform === platform))
      .filter((m) => filter.past(m.model_slug))
      .sort((a, b) => b.current_count - a.current_count)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growth.data, platform, filter.waarde, filter.modellen])

  /** Eén rij per nieuwe volger: een model met N nieuwe volgers levert N
   *  rijen op, elk met "+1" en de stand erna. Zo telt de tabel op tot het
   *  totaal dat de kop noemt (23), net als in de oude pagina. */
  const recent = useMemo(() => {
    const out: { slug: string; name: string; platform: string; after: number }[] = []
    for (const m of rows) {
      for (let i = m.new_followers; i >= 1; i--) {
        out.push({
          slug: m.model_slug,
          name: m.stage_name || m.model_slug,
          platform: m.platform,
          after: m.starting_count + i,
        })
      }
    }
    return out
  }, [rows])

  const byPlat = stats.data?.totals_by_platform ?? {}

  /* 04-10-2026 (Jordy, toggle-bug): Total Followers, New en Paying Fans volgen
     de FanCentro/F2F-keuze; de kaarten "Followers FC" en "Followers F2F" blijven
     de uitsplitsing. Met een gekozen model tellen alle kaarten alleen dat model
     (per_model uit platform-stats); zonder keuze de totalen van de server. */
  const kaart = (plat?: string) => {
    if (filter.waarde === 'all') {
      const t = plat ? byPlat[plat] : stats.data?.totals
      return { volgers: t?.followers, betalend: t?.paid_fans }
    }
    const rr = (stats.data?.per_model ?? [])
      .filter((r) => filter.past(r.model_slug) && (!plat || r.platform === plat))
    return {
      volgers: rr.reduce((s, r) => s + (r.follower_count || 0), 0),
      betalend: rr.reduce((s, r) => s + (r.paid_fan_count || 0), 0),
    }
  }
  const gekozenPlat = platform === 'all' ? undefined : platform
  const hoofd = kaart(gekozenPlat)
  const nieuw = filter.waarde === 'all' && platform === 'all'
    ? growth.data?.totals.new_followers
    : rows.reduce((s, m) => s + (m.new_followers || 0), 0)
  const spenderRijen = (spenders.data?.spenders ?? []).filter((s) => filter.past(s.model))
  const platNaam = gekozenPlat === 'fancentro' ? 'FanCentro' : gekozenPlat === 'f2f' ? 'F2F' : null

  /** Beste momenten: de vijf zwaarste (weekdag, uur)-cellen uit de matrix. */
  const best = useMemo(() => {
    const m = heat.data?.matrix ?? []
    const cells: { day: number; hour: number; v: number }[] = []
    m.forEach((row, d) => row.forEach((v, h) => cells.push({ day: d, hour: h, v })))
    return cells.sort((a, b) => b.v - a.v).slice(0, 5)
  }, [heat.data])

  const maxCell = useMemo(() => {
    const m = heat.data?.matrix ?? []
    return Math.max(1, ...m.flat())
  }, [heat.data])

  const strongestDay = useMemo(() => {
    const t = heat.data?.totals_by_weekday ?? []
    let i = 0
    t.forEach((v, k) => { if (v > (t[i] ?? 0)) i = k })
    return { label: DAYS[i] ?? '—', value: t[i] ?? 0 }
  }, [heat.data])

  const bestHour = useMemo(() => {
    const t = heat.data?.totals_by_hour ?? []
    let i = 0
    t.forEach((v, k) => { if (v > (t[i] ?? 0)) i = k })
    return { label: `${String(i).padStart(2, '0')}:00–${String(i + 1).padStart(2, '0')}:00`, value: t[i] ?? 0 }
  }, [heat.data])

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <Seg options={RANGES} value={days} onChange={setDays} />
        <ModelFilter filter={filter} />
      </div>

      <div className="grid kpis">
        <Kpi icon="↗" label="Total Followers" value={num(hoofd.volgers)}
             meta={platNaam ?? 'across both platforms'}
             chip={{ text: `+${num(nieuw)}`, dir: 'up' }} />
        <Kpi icon="♥" label="Followers FC" value={num(kaart('fancentro').volgers)} meta="FanCentro" />
        <Kpi icon="♦" label="Followers F2F" value={num(kaart('f2f').volgers)} meta="F2F" />
        <Kpi icon="✦" label="New (period)" value={`+${num(nieuw)}`}
             meta={platNaam ? `last window · ${platNaam}` : 'last window'} />
        <Kpi icon="€" label="Paying Fans" value={num(hoofd.betalend)}
             meta={platNaam ? `active subscribers · ${platNaam}` : 'active subscribers'} />
      </div>

      <Panel title="Followers per Model" flush
             right={<Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />}>
        {growth.loading ? <Loading /> : growth.error ? <ErrorNote error={growth.error} /> : (
          <Table cols={[
            { key: 'm', label: 'Model' }, { key: 'p', label: 'Platform' },
            { key: 'n', label: 'New', num: true }, { key: 'f', label: 'Followers', num: true },
          ]} emptyText="No models">
            {rows.map((m) => (
              <tr key={`${m.model_slug}-${m.platform}`}>
                <td><ModelNaam slug={m.model_slug} naam={m.stage_name} vet /></td>
                <td><Plat value={m.platform} /></td>
                <td className="num">
                  {m.new_followers
                    ? <Tag kind="ok">+{m.new_followers}</Tag>
                    : <span className="dim">0</span>}
                </td>
                <td className="num mono">{num(m.current_count)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Top Spenders" desc={`${num(filter.waarde === 'all' ? spenders.data?.total : spenderRijen.length)} total`} flush
             right={
               <button className="btn ghost sm" type="button" onClick={() => setShowAll((v) => !v)}>
                 {showAll ? 'Show top 10' : `Show all (${num(filter.waarde === 'all' ? spenders.data?.total : spenderRijen.length)})`}
               </button>
             }>
        {spenders.loading ? <Loading /> : (
          <Table cols={[
            { key: 'r', label: '#' }, { key: 'f', label: 'Fan' },
            { key: 'm', label: 'Model' }, { key: 'g', label: 'Gross', num: true },
          ]} emptyText="No spenders">
            {spenderRijen.slice(0, showAll ? undefined : 10).map((s, i) => (
              <tr key={`${s.fan_id}-${i}`}>
                <td className="rank">{i + 1}</td>
                <td>{s.username}</td>
                <td><ModelNaam naam={s.model} /></td>
                <td className="num mono">{eur(s.gross_spend)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Follower Scoreboard" flush>
        <Table cols={[
          { key: 'r', label: '#' }, { key: 'm', label: 'Model' },
          { key: 'p', label: 'Platform' }, { key: 'n', label: 'New', num: true },
          { key: 'f', label: 'Followers', num: true },
        ]} emptyText="No models">
          {(growth.data?.per_model ?? [])
            .filter((m) => filter.past(m.model_slug) && (platform === 'all' || m.platform === platform))
            .sort((a, b) => b.new_followers - a.new_followers || b.current_count - a.current_count)
            .map((m, i) => (
              <tr key={`sb-${m.model_slug}-${m.platform}`}>
                <td className="rank">{i + 1}</td>
                <td><ModelNaam slug={m.model_slug} naam={m.stage_name} /></td>
                <td><Plat value={m.platform} /></td>
                <td className="num">
                  {m.new_followers
                    ? <Tag kind="ok">+{m.new_followers}</Tag>
                    : <span className="dim">0</span>}
                </td>
                <td className="num mono">{num(m.current_count)}</td>
              </tr>
            ))}
        </Table>
      </Panel>

      {recent.length ? (
      <Panel
        title="Recent New Followers"
        desc={`${num(recent.length)} total · identities aren’t exposed by FC/F2F — only counts`}
        flush
      >
        <Table cols={[
          { key: 'm', label: 'Model' }, { key: 'p', label: 'Platform' },
          { key: 'f', label: 'Followers', num: true }, { key: 'a', label: 'After', num: true },
          { key: 'w', label: 'When' },
        ]} emptyText="No new followers in this window">
          {recent.map((m, i) => (
            <tr key={`rf-${m.slug}-${m.platform}-${i}`}>
              <td><ModelNaam slug={m.slug} naam={m.name} /></td>
              <td><Plat value={m.platform} /></td>
              <td className="num mono">+1</td>
              <td className="num mono">{num(m.after)}</td>
              <td className="mut2" style={{ fontSize: 12 }}>
                last {growth.data?.period_days ?? 1} day(s)
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
      ) : null}

      <Panel
        title="Follower Heatmap"
        desc={`New followers per (day-of-week × hour) — darker = more. ${num(heat.data?.total)} new in ${heat.data?.window_days ?? heatDays}d.`}
        right={<Seg options={HEAT_WINDOWS} value={heatDays} onChange={setHeatDays} />}
      >
        {heat.loading ? <Loading /> : heat.error ? <ErrorNote error={heat.error} /> : (
          <>
            <div className="tblwrap">
              <div className="heat">
                <div />
                {Array.from({ length: 24 }).map((_, h) => (
                  <div className="hh" key={h}>{String(h).padStart(2, '0')}</div>
                ))}
                {(heat.data?.matrix ?? []).map((row, d) => (
                  <>
                    <div className="hd" key={`d${d}`}>{DAYS[d]}</div>
                    {row.map((v, h) => (
                      <div
                        className="c"
                        key={`${d}-${h}`}
                        title={`${DAYS[d]} ${h}:00 — ${v}`}
                        style={{ background: `rgba(236,72,153,${(0.06 + (v / maxCell) * 0.9).toFixed(2)})` }}
                      />
                    ))}
                  </>
                ))}
              </div>
            </div>
            <div className="row mut2" style={{ fontSize: 12, marginTop: 10, gap: 10 }}>
              Fewest
              <span style={{
                flex: 1, height: 8, borderRadius: 20,
                background: 'linear-gradient(90deg,rgba(236,72,153,.08),var(--fc))',
              }} />
              Most followers
            </div>
          </>
        )}
      </Panel>

      <Panel title="Best times to post" flush>
        <div className="feed">
          {best.map((c, i) => (
            <div className="it" key={`${c.day}-${c.hour}`}>
              <span className="rank">{i + 1}</span>
              <div>
                <b>{DAYS[c.day]}</b>{' '}
                <span className="mut2">
                  {String(c.hour).padStart(2, '0')}:00–{String(c.hour + 1).padStart(2, '0')}:00
                </span>
              </div>
              <span className="tm"><Tag kind="ok">+{c.v}</Tag></span>
            </div>
          ))}
        </div>
        <div className="mini" style={{ margin: '14px 16px 4px' }}>
          <div className="f">
            <small>Strongest day</small>
            <b>{strongestDay.label} · +{num(strongestDay.value)}</b>
          </div>
          <div className="f"><small>Best hour slot</small><b>{bestHour.label}</b></div>
          <div className="f">
            <small>Total new ({heat.data?.window_days ?? heatDays}d)</small>
            <b>+{num(heat.data?.total)}</b>
          </div>
          <div className="f">
            <small>Avg per day</small>
            <b>+{((heat.data?.total ?? 0) / (heat.data?.window_days || 1)).toFixed(1)}</b>
          </div>
        </div>
      </Panel>
    </>
  )
}
